import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconEye, IconEyeOff, IconSettings } from '@tabler/icons-react';
import type { ShelfVisibility } from '@/api/types';
import { Chip } from '@/components/Chip';
import { Skeleton } from '@/components/Skeleton';
import { Modal } from '@/components/Modal';
import { ShelfRow } from '@/features/shelf/ShelfRow';
import { ShelfBooks } from '@/features/shelf/ShelfBooks';
import { ShelfFriends } from '@/features/shelf/ShelfFriends';
import { EmptyShelf } from '@/features/shelf/EmptyShelf';
import { api } from '@/api/endpoints';
import type { ShelfItem } from '@/api/types';
import { useAuth } from '@/store/auth';
import { useUI } from '@/store/ui';
import { haptic } from '@/lib/telegram';
import { trackEvent } from '@/lib/analytics';

export default function Shelf() {
  const { t } = useTranslation();
  const user = useAuth(s => s.user);
  const patchUser = useAuth(s => s.patchUser);
  const showToast = useUI(s => s.showToast);

  const [filter, setFilter] = useState<'all' | 'books' | 'friends'>('all');
  const [items, setItems] = useState<ShelfItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.shelf.me()
      .then(({ data }) => setItems(data))
      .catch(() => showToast(t('errors.network'), 'error'))
      .finally(() => setLoading(false));
  }, [t, showToast]);

  const setVisibility = async (v: ShelfVisibility) => {
    haptic.select();
    try {
      await api.me.setShelfVisibility(v);
      patchUser({ shelf_visibility: v });
    } catch { /* swallow */ }
  };

  const onHide = async (cardId: string, hide: boolean) => {
    try {
      await api.cards.setBookmarkVisibility(cardId, hide);
      setItems(items => items.map(it => it.id === cardId ? { ...it, hidden_from_public: hide } : it));
    } catch { /* swallow */ }
  };

  const onDelete = async (cardId: string) => {
    try {
      await api.cards.removeBookmark(cardId);
      setItems(items => items.filter(it => it.id !== cardId));
    } catch { /* swallow */ }
  };

  const visible = items.filter(it => filter === 'all' ? true : filter === 'books' ? true : it.topic_id === filter);

  return (
    <div className="flex-1 flex flex-col bg-bg min-h-0">
      <header className="px-4 pt-4 pb-3 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-medium">{t('shelf.title')}</h1>
            <button
              aria-label="toggle shelf visibility"
              className="w-7 h-7 grid place-items-center rounded-pill text-text-muted hover:text-text"
              onClick={() => setSettingsOpen(true)}
            >
              {user?.shelf_visibility === 'hidden' ? <IconEyeOff size={18} /> : <IconEye size={18} />}
            </button>
          </div>
          <p className="text-xs text-text-muted mt-1">{t('shelf.saved_count', { n: items.length })}</p>
        </div>
        <button onClick={() => setSettingsOpen(true)} className="w-9 h-9 grid place-items-center rounded-pill hover:bg-surface" aria-label="settings">
          <IconSettings size={20} />
        </button>
      </header>

      <Modal open={settingsOpen} onClose={() => setSettingsOpen(false)} title={t('shelf.settings_title', { defaultValue: 'Настройки полки' })} height={240}>
        <div className="px-4 py-3 space-y-2">
          {([
            { value: 'all',     label: t('shelf.visibility_all') },
            { value: 'friends', label: t('shelf.visibility_friends') },
            { value: 'hidden',  label: t('shelf.visibility_hidden') },
          ] as { value: ShelfVisibility; label: string }[]).map(opt => (
            <button
              key={opt.value}
              onClick={() => setVisibility(opt.value)}
              className={`w-full flex items-center justify-between px-3 py-3 rounded-xl border transition ${
                user?.shelf_visibility === opt.value
                  ? 'bg-accent/10 border-accent text-text'
                  : 'bg-surface border-transparent text-text-muted'
              }`}
            >
              <span className="text-sm font-medium">{opt.label}</span>
              <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                user?.shelf_visibility === opt.value ? 'border-accent' : 'border-text-disabled'
              }`}>
                {user?.shelf_visibility === opt.value && (
                  <span className="w-2 h-2 rounded-full bg-accent" />
                )}
              </span>
            </button>
          ))}
        </div>
      </Modal>

      {/* Фильтр-чипы */}
      <div className="px-4 pb-3 flex gap-2 overflow-x-auto scrollbar-none">
        <Chip active={filter === 'all'} count={items.length} onClick={() => setFilter('all')}>
          {t('shelf.all')}
        </Chip>
        <Chip active={filter === 'books'} onClick={() => setFilter('books')}>
          {t('shelf.by_books')}
        </Chip>
        <Chip active={filter === 'friends'} onClick={() => setFilter('friends')}>
          {t('shelf.by_friends')}
        </Chip>
        {/* TODO: чипы по темам пользователя */}
      </div>

      <div
        className="flex-1 overflow-y-auto px-4 pb-4"
        onScroll={(e) => {
          const el = e.currentTarget;
          if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
            if (!el.dataset.scrollEndSent) {
              el.dataset.scrollEndSent = '1';
              trackEvent('scroll_end', { screen: 'shelf' });
            }
          } else {
            delete el.dataset.scrollEndSent;
          }
        }}
      >
        {filter === 'friends' ? (
          <ShelfFriends />
        ) : filter === 'books' ? (
          loading ? (
            <div className="space-y-3"><Skeleton height={88} /><Skeleton height={88} /></div>
          ) : (
            <ShelfBooks items={items} onHide={onHide} onDelete={onDelete} />
          )
        ) : loading ? (
          <div className="space-y-3"><Skeleton height={88} /><Skeleton height={88} /><Skeleton height={88} /></div>
        ) : visible.length === 0 ? (
          <EmptyShelf title={t('shelf.empty')} />
        ) : (
          <div className="space-y-3">
            {visible.map(item => (
              <ShelfRow key={item.id} item={item} onHide={onHide} onDelete={onDelete} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
